import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  TextField,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { FC, useEffect, useMemo, useState } from "react";
import { Collaborator, GetCompanies } from "src/apis/company";

type Company = {
  id: number;
  trade_name?: string | null;
  social_reason?: string | null;
  document?: string | null;
  oculto?: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSoftDelete: (targetCompanyId: number) => void | Promise<void>;
  onHardDelete?: () => void | Promise<void>;
  collaborator?: Collaborator | null;
  isAdmin?: boolean;
  loading?: boolean;
  /** opcional: id da empresa atual */
  currentCompanyId?: number | string;
};

const DeleteCollaboratorDialog: FC<Props> = ({
  open,
  onClose,
  onSoftDelete,
  onHardDelete,
  collaborator,
  isAdmin = false,
  loading = false,
  currentCompanyId,
}) => {
  const id = collaborator?.id_colaborador;
  const nome = collaborator?.nome;

  // destino fixo para EMPRESA
  const FORCED_TARGET_ID = 45;

  // ---------- Fluxo ADMIN (pode escolher) ----------
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [manualId, setManualId] = useState<string>("");
  const [acOpen, setAcOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!open) return;

    // reset
    setCompaniesError("");
    setSelectedCompany(null);
    setManualId("");
    setInputValue("");
    setAcOpen(false);

    if (!isAdmin) {
      // EMPRESA: não carrega lista
      setCompanies([]);
      setCompaniesLoading(false);
      return;
    }

    (async () => {
      try {
        setCompaniesLoading(true);
        const res = await GetCompanies();
        const list = Array.isArray(res?.data)
          ? (res.data as Company[]).filter((company) => !company?.oculto)
          : [];
        setCompanies(list);
        if (list.length > 0) setAcOpen(true);
      } catch (e: any) {
        setCompaniesError(e?.message || "Falha ao buscar empresas.");
      } finally {
        setCompaniesLoading(false);
      }
    })();
  }, [open, isAdmin]);

  // Admin: destino vem da seleção/input
  const adminTargetCompanyId = useMemo(() => {
    const chosen = selectedCompany?.id ?? Number(manualId);
    return Number(chosen);
  }, [selectedCompany, manualId]);

  // Empresa: destino é fixo (45)
  const effectiveTargetCompanyId = isAdmin ? adminTargetCompanyId : FORCED_TARGET_ID;

  // Para ADMIN seguimos validando; para EMPRESA, não bloqueamos por “mesma empresa”
  const targetIsValid = useMemo(() => {
    if (!isAdmin) return true; // EMPRESA: sempre habilitado (exceto loading)
    const current = Number(currentCompanyId);
    const dest = Number(effectiveTargetCompanyId);
    return Number.isInteger(dest) && dest > 0 && (!Number.isFinite(current) || dest !== current);
  }, [isAdmin, effectiveTargetCompanyId, currentCompanyId]);

  const handleMove = async () => {
    if (loading) return;
    // EMPRESA: sempre manda 45; ADMIN: manda o escolhido
    const dest = isAdmin ? effectiveTargetCompanyId : FORCED_TARGET_ID;
    await onSoftDelete(Number(dest));
  };

  const handleClose = () => {
    if (loading) return;
    setSelectedCompany(null);
    setManualId("");
    setAcOpen(false);
    onClose();
  };

  const optionLabel = (opt: Company | string): string => {
    if (!opt) return "";
    if (typeof opt === "string") return opt;
    const name = opt.trade_name || opt.social_reason || `Empresa ${opt.id}`;
    const doc = opt.document ? ` • ${opt.document}` : "";
    return `${name}${doc} — ID ${opt.id}`;
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : handleClose} fullWidth maxWidth="sm" keepMounted>
      <DialogTitle>Gerenciar colaborador</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1">
            Selecione a ação para <strong>{nome}</strong> (ID <strong>{id}</strong>).
          </Typography>

          {/* Mover colaborador (Soft delete) */}
          <Stack spacing={1.5} sx={{ border: "1px solid", borderColor: "divider", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1">Mover colaborador</Typography>

            {isAdmin ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Escolha a empresa de destino ou informe o ID manualmente. A operação marcará <em>desligado</em>=true.
                </Typography>

                <Autocomplete<Company, false, false, false>
                  options={companies}
                  value={selectedCompany}
                  onChange={(_, v) => setSelectedCompany(v)}
                  inputValue={inputValue}
                  onInputChange={(_, v) => setInputValue(v)}
                  getOptionLabel={optionLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  loading={companiesLoading}
                  open={acOpen}
                  onOpen={() => setAcOpen(true)}
                  onClose={() => setAcOpen(false)}
                  openOnFocus
                  autoHighlight
                  loadingText="Carregando empresas…"
                  noOptionsText={companiesError || "Nenhuma empresa encontrada"}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Empresa de destino"
                      placeholder="Digite o nome ou selecione"
                      error={Boolean(companiesError)}
                      helperText={companiesError || "Pesquise pelo nome fantasia ou razão social"}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {companiesLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <TextField
                  label="Ou insira o ID da empresa"
                  type="number"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  disabled={loading}
                  inputProps={{ min: 1, step: 1 }}
                  error={!!manualId && !(Number.isInteger(adminTargetCompanyId) && adminTargetCompanyId > 0)}
                  helperText={
                    !!manualId && !(Number.isInteger(adminTargetCompanyId) && adminTargetCompanyId > 0)
                      ? "Informe um ID válido (> 0)."
                      : "Opcional: substitui a seleção acima"
                  }
                  fullWidth
                />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Esta ação vai arquivar o colaborador (destino fixo ID <strong>{FORCED_TARGET_ID}</strong>) e marcar{" "}
                  <em>desligado</em>=true.
                </Typography>
              </>
            )}

            <Button
              variant="contained"
              color="warning"
              onClick={handleMove}
              disabled={loading || !targetIsValid}
              sx={{ alignSelf: "flex-start" }}
            >
              {loading ? "Movendo..." : isAdmin ? "Mover colaborador" : "Arquivar colaborador"}
            </Button>
          </Stack>

          {/* Deletar colaborador (hard delete) */}
          <Stack spacing={1.5} sx={{ border: "1px solid", borderColor: "divider", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1">Deletar colaborador</Typography>
            <Typography variant="body2" color="text.secondary">
              Remove definitivamente o colaborador e registros relacionados.
            </Typography>

            {isAdmin && onHardDelete ? (
              <Button
                variant="contained"
                color="error"
                onClick={onHardDelete}
                disabled={loading}
                sx={{ alignSelf: "flex-start" }}
              >
                {loading ? "Deletando..." : "Deletar colaborador"}
              </Button>
            ) : (
              <Typography variant="body2" color="text.disabled">
                Apenas administradores podem deletar permanentemente.
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCollaboratorDialog;
